import classNames from "classnames";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Modal } from "PFComponents/modal";
import { Typography } from "PFComponents/typography";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useOwnerCreate } from "PFCore/hooks/queries/owners";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Activity } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./become_coowner_button.module.scss";

type BecomeCoownerButtonProps = {
  activity: Activity;
  small?: boolean;
  className?: string;
};

export const BecomeCoownerButton = ({ activity, small, className }: BecomeCoownerButtonProps) => {
  const [showModal, setShowModal] = useState(false);
  const { mutateAsync: createOwner } = useOwnerCreate();

  const { data: currentProfile } = useCurrentProfile();
  const { id: currentProfileId } = currentProfile;
  const { t } = useTranslation("activities");
  const template = useTemplate(activity.template_id);

  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const handleOKClick = async () => {
    try {
      await createOwner({ profileId: currentProfileId, activityId: activity.id });
      showGrowl({
        message: t("show.parts.addedAsCoOwner"),
        kind: "success"
      });
      setShowModal(false);
    } catch (error) {
      const errorMessage = getFirstError(error);
      showGrowl({
        message: errorMessage || t("show.parts.unknownError"),
        kind: "error"
      });
    }
  };

  const labels = {
    [TemplateKey.Engagement]: t("show.parts.addYourselfAsCoOwner.engagement"),
    [TemplateKey.Role]: t("show.parts.addYourselfAsCoOwner.role"),
    [TemplateKey.AuditEngagement]: t("show.parts.addYourselfAsCoOwner.auditEngagement"),
    [TemplateKey.AuditRole]: t("show.parts.addYourselfAsCoOwner.auditRole")
  };

  return (
    <>
      <Button
        text={t("show.parts.becomeCoOwner")}
        onClick={() => setShowModal(true)}
        className={classNames(css.button, className)}
        small={small}
      />
      {showModal && (
        <Modal
          title={t("show.parts.becomeCoOwner")}
          onClose={() => setShowModal(false)}
          onOK={handleOKClick}
          showOKButton
        >
          <Typography withMargin variant="bodyRegular">
            {template ? labels[template.key] : t("show.parts.addYourselfAsCoOwner.activity")}
          </Typography>
        </Modal>
      )}
    </>
  );
};
